/**
 * StatusBadge Component
 * Unified status badge for project, request, and contractor statuses
 */

const statusConfig: Record<string, { bg: string; label: string }> = {
	// Project statuses
	draft: { bg: "bg-muted text-foreground", label: "Draft" },
	seeking_designer: {
		bg: "bg-accent text-accent-foreground",
		label: "Seeking Designer",
	},
	awaiting_funding: {
		bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning",
		label: "Awaiting Funding",
	},
	in_progress: { bg: "bg-primary/10 text-primary", label: "In Progress" },
	cancelled: { bg: "bg-destructive/10 text-destructive", label: "Cancelled" },

	// Request statuses
	pending: { bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning", label: "Pending" },
	proposal_submitted: {
		bg: "bg-dashboard-pill-info-bg text-dashboard-info-text",
		label: "Proposal Sent",
	},
	accepted: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Accepted" },
	rejected: { bg: "bg-dashboard-pill-risk-bg text-dashboard-danger-strong", label: "Rejected" },

	// Contractor statuses
	invited: { bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning", label: "Invited" },
	interested: { bg: "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200", label: "Interested" },
	quote_submitted: {
		bg: "bg-dashboard-pill-info-bg text-dashboard-info-text",
		label: "Quote Submitted",
	},
	hired: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Hired" },
	completion_requested: {
		bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning",
		label: "Completion Pending",
	},
	completed: { bg: "bg-primary/10 text-primary", label: "Completed" },
	removed: { bg: "bg-dashboard-pill-risk-bg text-dashboard-danger-strong", label: "Removed" },
	declined: { bg: "bg-dashboard-pill-risk-bg text-dashboard-danger-strong", label: "Declined" },

	// Task statuses
	todo: { bg: "bg-muted text-foreground", label: "To Do" },

	// Milestone payment statuses
	not_paid: { bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning", label: "Not Paid" },
	paid: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Paid" },

	// Funding statuses
	unfunded: { bg: "bg-muted text-muted-foreground", label: "Unfunded" },
	funded: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Funded" },
	released: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Released" },
	held: { bg: "bg-dashboard-pill-info-bg text-dashboard-info-text", label: "Held" },
	disputed: { bg: "bg-dashboard-pill-risk-bg text-dashboard-danger-strong", label: "Disputed" },
	refunded: { bg: "bg-muted text-muted-foreground", label: "Refunded" },

	// BOQ statuses
	sent: { bg: "bg-dashboard-pill-info-bg text-dashboard-info-text", label: "Sent" },
	change_requested: { bg: "bg-dashboard-pill-awaiting-bg text-dashboard-warning", label: "Changes Requested" },
	approved: { bg: "bg-dashboard-pill-done-bg text-dashboard-success-strong", label: "Approved" },
	locked: { bg: "bg-muted text-muted-foreground", label: "Locked" },
};

const defaultConfig = { bg: "bg-muted text-muted-foreground", label: "" };

interface StatusBadgeProps {
	status: string;
	/** Replaces the label `status` maps to; the colour still comes from `status`. */
	label?: string;
	className?: string;
}

export function StatusBadge({
	status,
	label,
	className,
}: StatusBadgeProps): React.ReactElement {
	const config = statusConfig[status] || { ...defaultConfig, label: status };
	return (
		<span
			className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${className ?? ""}`}
		>
			{label ?? config.label}
		</span>
	);
}
